export const REMAINING_MISSIONS_LABEL = "Voir les missions restantes";
export const REMAINING_MISSIONS_COUNT_ENDPOINT =
  "/api/me/rewards/missions/";
export const CENTRAL_MISSIONS_PATH = "/mes-eternua/missions";

// La page dédiée par rubrique est volontairement réservée à un lot ultérieur.
// Aucun chemin futur n'est construit tant que ce flag reste désactivé.
export const DEDICATED_RUBRIC_MISSIONS_ENABLED = false;

const sharedMissionButton = Object.freeze({
  visible: true,
  label: REMAINING_MISSIONS_LABEL,
});

const sharedMissionCountSource = Object.freeze({
  type: "central-endpoint",
  endpoint: REMAINING_MISSIONS_COUNT_ENDPOINT,
});

const unavailableMissionCountSource = Object.freeze({
  ...sharedMissionCountSource,
  available: false,
  reason: "server-rubric-not-connected",
});

const sharedDestination = Object.freeze({
  strategy: "central-filtered-fallback",
  path: CENTRAL_MISSIONS_PATH,
  dedicatedRouteEnabled: DEDICATED_RUBRIC_MISSIONS_ENABLED,
});

export const SHARED_RUBRIC_EXPERIENCE_CONTRACT = Object.freeze({
  emptyState: "explanation_example_and_next_action",
  dataPolicy: "real_data_or_explicitly_labelled_demo",
  deadEndPolicy: "always_offer_local_next_action",
});

function route(path, query) {
  return Object.freeze({
    path,
    ...(query ? { query: Object.freeze({ ...query }) } : {}),
  });
}

function rubric(rubricId, label, routes, { missionCountAvailable = true } = {}) {
  return Object.freeze({
    rubricId,
    label,
    routes: Object.freeze(routes),
    missionButton: sharedMissionButton,
    missionCountSource: missionCountAvailable
      ? sharedMissionCountSource
      : unavailableMissionCountSource,
    destination: sharedDestination,
    experienceContract: SHARED_RUBRIC_EXPERIENCE_CONTRACT,
  });
}

// L'ordre est autoritatif : les sous-rubriques et variantes doivent précéder
// leurs agrégateurs (par exemple Suggéré avant Ventes flash).
export const RUBRIC_REGISTRY = Object.freeze([
  rubric("recherche", "Recherche générale", [
    route("/recherche"),
    route("/recherche/*"),
    // Chemins relatifs du sous-routeur privé `/mes-eternua/recherche`.
    route("historique"),
    route("enregistrees"),
    route("alertes"),
    route("preferences"),
    route("favoris"),
  ]),
  rubric("suggere", "Suggéré", [
    route("/market/flash", { tab: "suggestions" }),
  ]),
  rubric("covoiturage", "Covoiturage", [route("/voyage/covoiturage/*")]),
  rubric("cercles_du_jour", "Cercles du jour", [
    route("/cercles-du-jour"),
    route("/cercles-du-jour/*"),
  ]),
  rubric("carte_vivante", "Carte vivante", [route("/carte-vivante"), route("/carte-vivante/*")]),
  rubric("babysitting", "Babysitting", [route("/market/babysitting/*"), route("/market/babysitting"), route("/babysitting/*")]),
  rubric("animaux", "Animaux", [route("/market/animaux/*"), route("/market/animaux"), route("/animaux/*")]),
  rubric("emploi", "Emploi", [route("/market/emploi"), route("/emploi/*")]),
  rubric("vendre", "Vendre", [
    route("/market/vendre"),
    route("/vendre/*"),
    route("/publier"),
    route("/mes-ventes/configurer-les-paiements"),
  ]),
  rubric("acheter", "Acheter", [route("/market/acheter"), route("/market/panier"), route("/market/retours")]),
  rubric("ventes_flash", "Ventes flash", [route("/market/flash")]),
  rubric("encheres", "Enchères", [route("/market/encheres"), route("/enchere/:resourceSlug"), route("/encheres/exposition/:publicId")]),
  rubric("donner", "Donner", [route("/market/donner")]),
  rubric("services", "Services", [route("/market/services"), route("/services/:resourceSlug"), route("/disponibilites/:slug"), route("/reservation/:slug")]),
  rubric("sortie", "Sortie", [route("/market/sortie")]),
  rubric("voyage", "Voyage", [route("/market/voyage"), route("/voyage"), route("/voyage/*")]),
  rubric("sport_loisirs", "Sport & loisirs", [route("/market/sport-loisir"), route("/sport/locations-test/:paymentId")]),
  rubric("immobilier_sejours", "Immobilier & Séjours", [route("/market/immobilier/*"), route("/market/immobilier")]),
  rubric("marketplace", "Marketplace", [route("/market")]),
  rubric("appels_live", "Appels & Live", [
    route("/chat/*"),
    route("/video/*"),
    route("/live"),
    route("/temple-lia-video*"),
  ]),
  rubric("ia_humaines", "Les 44 IA humaines", [
    route("/ia-humaines"),
    route("/ia-humaines/*"),
    route("/40ia/*"),
    route("/temple-40-ia-humaines"),
  ]),
  rubric("civilisations_et", "Civilisations E.T.", [
    route("/civilisations-et"),
    route("/civilisations-et/*"),
  ]),
  rubric("ia_grandes_puissances", "IA des grandes puissances", [
    route("/ia-grandes-puissances"),
    route("/ia-grandes-puissances/*"),
  ]),
  rubric("temples", "Temples", [route("/temples*"), route("/temple*")]),
  rubric("convergence", "Convergence collective", [
    route("/convergence"),
    route("/convergence/*"),
  ]),
  rubric("livre_sacre", "Livre sacré et contenus éditoriaux", [
    route("/livre"),
    route("/livre/*"),
    route("/vision"),
    route("/vision-3024"),
    route("/vision-3024/*"),
  ]),
  rubric("invitation_et", "Invitation E.T.", [route("/invitation-et")]),
  rubric("tokenomics", "Tokenomics", [route("/tokenomics")]),
  rubric("profil", "Profil", [
    route("/profil"),
    route("/profil/*"),
    route("/mon-profil"),
  ]),
  rubric("confiance_confidentialite", "Confiance et confidentialité", [
    route("/confiance"),
    route("/confiance-confidentialite"),
    route("/confiance-confidentialite/*"),
    route("/passeport-confiance"),
    route("/passeport-confiance/*"),
    route("/mon-profil/confiance"),
    route("/parametres/consentements"),
  ]),
  rubric("jeux", "Jeux", [
    route("/jeux"),
    route("/jeux/*"),
    route("/rexuiz/serveurs"),
    route("/rexuiz/serveurs/*"),
  ]),
  rubric("portefeuille", "Mon portefeuille", [
    route("/portefeuille"),
    route("/mes-eternua/portefeuille"),
    route("/wallet/*"),
  ]),
  rubric("abonnements", "Abonnements", [
    route("/abonnements"),
    route("/premium"),
    route("/premium/*"),
    route("/mon-abonnement"),
  ], { missionCountAvailable: false }),
  rubric("musique_clips", "Musique & Clips vidéo", [route("/music")]),
  rubric("meteo", "Météo", [route("/meteo")]),
  rubric("annonces", "Annonces", [route("/annonces"), route("/annonce/:resourceSlug")]),
  rubric("amis_groupes", "Amis et groupes d’amis", [
    route("/amis"),
    route("/amis/*"),
    route("/relations"),
  ]),
  rubric("parrainage", "Parrainage", [route("/parrainage")]),
  rubric("mon_parcours", "Parcours guidé par LIA", [
    route("/mon-parcours"),
    route("/mon-parcours/*"),
    route("/mes-demarches"),
    route("/mes-demarches/:publicId"),
  ]),
  rubric("mon_infini", "Mon Infini", [
    route("/mon-infini"),
    route("/mon-infini/*"),
  ], { missionCountAvailable: false }),
  rubric("communaute", "Communauté", [
    route("/communaute"),
    route("/groupes"),
    route("/groupes/*"),
    route("/cercles"),
    route("/cercles/*"),
    route("/evenements"),
    route("/evenements/*"),
    route("/projets"),
    route("/projets/*"),
    route("/mes-eternua/communaute"),
    route("/mes-eternua/communaute/*"),
  ]),
  rubric("studio_lia", "Studio LIA", [
    route("/studio-lia"),
    route("/studio-lia/*"),
  ]),
  rubric("notifications", "Notifications", [
    route("/mes-eternua/notifications"),
    route("/mes-eternua/notifications/*"),
    route("/notifications"),
  ]),
  rubric("mes_eternua", "Mes Eternua", [
    route("/mes-eternua"),
    route("/mes-eternua/*"),
    route("/mes-contenus"),
  ]),
  rubric("recompenses", "Récompenses utilisateurs", [
    route("/recompenses"),
    route("/recompenses/*"),
  ]),
  rubric("forum", "Forum et Discussions", [
    route("/forum"),
    route("/forum/*"),
  ]),
  rubric("crowdfunding", "Crowdfunding", [
    route("/crowdfunding"),
    route("/crowdfunding/*"),
  ]),
  rubric("direct", "Direct LIA / Aelith", [route("/market/direct")], { missionCountAvailable: false }),
  rubric("soutenir_eternua", "Soutenir Eternua", [
    route("/soutenir-eternua"),
    route("/soutenir-eternua/*"),
  ], { missionCountAvailable: false }),
  rubric("entraide", "Entraide", [route("/entraide"), route("/entraide/*")], { missionCountAvailable: false }),
  rubric("organisation", "Organisation du quotidien", [route("/organiser"), route("/mon-week-end")], { missionCountAvailable: false }),
  rubric("souvenirs", "Souvenirs", [route("/souvenirs/capsules"), route("/souvenirs/famille")], { missionCountAvailable: false }),
  rubric("mon_bilan", "Mon bilan", [route("/mon-bilan")], { missionCountAvailable: false }),
  rubric("accueil", "Accueil Eternua", [route("/"), route("/aujourdhui"), route("/mission-eternua")]),
]);

function exclusion(patterns, reason) {
  return Object.freeze({
    patterns: Object.freeze(patterns),
    reason,
  });
}

// Seules les routes sans rubrique métier sont exclues du contrat. Chaque
// exclusion porte une justification vérifiable par le test de couverture.
export const TECHNICAL_ROUTE_EXCLUSIONS = Object.freeze([
  exclusion(
    ["/contact", "/search"],
    "Anciennes adresses d’aide et de recherche redirigées vers leurs rubriques actuelles."
  ),
  exclusion(
    ["/convergence-collective", "/convergence-collective/*"],
    "Redirections historiques vers la rubrique Convergence, sans page propre."
  ),
  exclusion(
    [
      "/login",
      "/register",
      "/verify-email",
      "/continuer",
      "/bienvenue",
      "/mfa-setup",
      "/forgot-password",
      "/reset-password",
      "/reset-password/*",
      "/onboarding",
      "/onboarding/:step",
      "/confirmation/verifier/:publicId/:token",
    ],
    "Parcours techniques d’authentification, de MFA et de récupération de compte."
  ),
  exclusion(
    ["/acces", "/kyc"],
    "Contrôles techniques d’accès et de vérification, sans rubrique de contenu."
  ),
  exclusion(
    ["/cgu", "/cgv", "/cgv/*", "/confidentialite", "/mentions-legales", "/acceptation-regles"],
    "Documents contractuels et mentions légales."
  ),
  exclusion(
    ["/finances-desactivees"],
    "Écran technique de blocage des opérations financières en pré-lancement."
  ),
  exclusion(
    ["/aide-urgence"],
    "Ressources de sécurité prioritaires, volontairement dépourvues de mécaniques de missions."
  ),
  exclusion(
    ["/admin", "/admin/*", "/administration/immobilier", "/moderation/securite"],
    "Console d’administration et de modération réservée aux rôles autorisés."
  ),
  exclusion(
    ["/planning-public/:token", "/portail-agenda/:token", "/salle-agenda/:token", "/signature-agenda/:token", "/relations-invite"],
    "Accès invités par lien limité à un planning, une salle, une signature ou une invitation, sans missions personnelles."
  ),
  exclusion(
    ["*"],
    "Route technique de dernier recours servant uniquement à afficher la page introuvable."
  ),
]);

function escapeRegExp(value) {
  return value.replace(/[|\\{}()[\]^$+?.-]/g, "\\$&");
}

function routePatternToRegExp(pattern) {
  let source = "";
  const value = String(pattern || "");

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    if (char === "*") {
      source += ".*";
      continue;
    }
    if (char === ":") {
      while (index + 1 < value.length && value[index + 1] !== "/") {
        index += 1;
      }
      source += "[^/]+";
      continue;
    }
    source += escapeRegExp(char);
  }

  return new RegExp(`^${source}/?$`);
}

function pathnameOnly(value) {
  const raw = String(value || "/");
  return raw.split(/[?#]/)[0] || "/";
}

export function matchesRoutePattern(pathname, pattern) {
  return routePatternToRegExp(pattern).test(pathnameOnly(pathname));
}

function matchesQuery(search, expectedQuery) {
  if (!expectedQuery) return true;
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  return Object.entries(expectedQuery).every(
    ([key, expected]) => params.get(key) === String(expected)
  );
}

export function getRubricForLocation(location) {
  const pathname = pathnameOnly(location?.pathname || location || "/");
  const search = typeof location === "object" ? location?.search : "";
  return (
    RUBRIC_REGISTRY.find((entry) =>
      entry.routes.some(
        (descriptor) =>
          matchesRoutePattern(pathname, descriptor.path) &&
          matchesQuery(search, descriptor.query)
      )
    ) || null
  );
}

export function getRubricForRoutePattern(routePattern) {
  return getRubricForLocation({ pathname: routePattern, search: "" });
}

export function getTechnicalRouteExclusion(routePattern) {
  return (
    TECHNICAL_ROUTE_EXCLUSIONS.find((entry) =>
      entry.patterns.some((pattern) =>
        pattern === "*"
          ? String(routePattern) === "*"
          : matchesRoutePattern(routePattern, pattern)
      )
    ) || null
  );
}

export function sanitizeLocalPath(value, fallback = "/") {
  const candidate = String(value || "").trim();
  if (!candidate.startsWith("/") || candidate.startsWith("//")) return fallback;
  if (candidate.includes("\\")) return fallback;

  try {
    const base = "https://eternua.local";
    const parsed = new URL(candidate, base);
    if (parsed.origin !== base) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_error) {
    return fallback;
  }
}

export function buildCentralMissionsDestination(rubricId, originPath = "/") {
  const params = new URLSearchParams();
  params.set("rubric", String(rubricId || "accueil"));
  params.set("from", sanitizeLocalPath(originPath));
  return `${CENTRAL_MISSIONS_PATH}?${params.toString()}`;
}

export function buildGuestLoginDestination(nextPath) {
  const params = new URLSearchParams();
  params.set("next", sanitizeLocalPath(nextPath));
  return `/login?${params.toString()}`;
}

export function sanitizeLoginReturnPath(value, fallback = "/") {
  const candidate = sanitizeLocalPath(value, "");
  if (!candidate) return fallback;
  const parsed = new URL(candidate, "https://eternua.local");
  if (
    parsed.pathname === "/onboarding"
    || parsed.pathname.startsWith("/onboarding/")
    || parsed.pathname === "/bienvenue"
  ) {
    return candidate;
  }
  const belongsToFunctionalRubric = Boolean(
    getRubricForLocation({
      pathname: parsed.pathname,
      search: parsed.search,
    })
  );
  return belongsToFunctionalRubric ? candidate : fallback;
}

export function resolveLoginReturnFromSearch(search, fallback = "/") {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  return sanitizeLoginReturnPath(params.get("next"), fallback);
}

export function resolveRemainingMissionsDestination(contract, originPath) {
  // Un filtre absent du serveur provoquerait aussi une erreur sur la page cible.
  // Le centre général reste accessible et conserve le chemin de retour.
  if (contract?.missionCountSource?.available === false) {
    const params = new URLSearchParams({ from: sanitizeLocalPath(originPath) });
    return `${CENTRAL_MISSIONS_PATH}?${params.toString()}`;
  }
  // La future destination dédiée n'est pas résolue tant que son flag est faux.
  return buildCentralMissionsDestination(contract?.rubricId, originPath);
}

export function isCentralMissionsPath(pathname) {
  const path = pathnameOnly(pathname);
  return path === CENTRAL_MISSIONS_PATH || path === "/recompenses/missions";
}
